/* ISO 3166-1 — the 249 officially assigned country codes.
 *
 * The route option's origin and destination were free text, and free text is how
 * the source workbook ended up holding the same country under several spellings.
 * This is the same substitution incoterms.ts makes, and for the same reason: a
 * value that names a jurisdiction is a CODE with a name attached, not a phrase
 * somebody types.
 *
 * WHY THE FULL 249 AND NOT ASA'S OWN LANES. A shortlist of the twenty countries
 * ASA ships to today would be shorter to read and would have to be edited by a
 * developer the first time a client asks for somewhere new. The standard is
 * fixed, published, and costs ~12 KB; the lane frequency belongs in the ordering
 * of the list, not in its membership.
 *
 * Officially assigned only — not the transitionally reserved (SU, YU), not the
 * exceptionally reserved (EU, UK), not ICU's non-country regions (UN, EZ, QO).
 * ICU's region list runs to 280 entries for that reason and is NOT the standard;
 * the codes here are the standard's, and only the NAMES come from ICU, generated
 * rather than typed so no country is misspelled in either language.
 *
 * `nameRu` is ICU's Russian, held here rather than in the i18n catalogue. The
 * locale files carry OUR sentences, which a translator may reword; these are
 * exonyms fixed by usage, all 249 of them, and putting them in en.ts/ru.ts would
 * double both files with strings nobody should be editing.
 *
 * Alpha-3 and numeric are carried because customs paperwork uses them — the
 * declaration wants RUS, not RU — and because a picker holding only alpha-2
 * forces the document layer to keep a second mapping table.
 *
 * As with incoterms.ts, this is a CLIENT-SIDE narrowing. The server stores
 * origin and destination as bounded text (migration 0020) and accepts anything
 * non-blank, so a route option saved earlier as "Shanghai" — or written by the
 * importer — still reads back and still prints. Nothing here rejects stored
 * data; it only shapes what is offered next.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 — the stored value. */
  code: string
  /** ISO 3166-1 alpha-3, as customs declarations write it. */
  alpha3: string
  /** ISO 3166-1 numeric, zero-padded to three digits. */
  numeric: string
  /** The E.164 country calling code WITHOUT the plus — "7", "86", "993".
   *
   *  Empty for the seven codes that name a place with no telephone
   *  administration of its own (AQ, BV, GS, HM, PN, TF, UM); those are countries
   *  on a routing and never a phone number, so the phone picker skips them
   *  rather than offering a code that cannot be dialled. Generated from
   *  libphonenumber-js metadata, not typed. */
  dial: string
  name: string
  nameRu: string
  /** The regional-indicator pair. A TEXT fallback only: the pickers draw the
   *  circular SVG from `public/flags/` (see CircleFlag), because Windows ships
   *  no emoji-flag glyphs and renders this as a boxed "KZ". Kept because it
   *  costs nothing and reads correctly everywhere the SVG cannot go — a title
   *  attribute, a copied cell, a plain-text export. */
  flag: string
}

export const COUNTRIES: readonly Country[] = [
  { code: 'AD', alpha3: 'AND', numeric: '020', dial: '376', name: "Andorra", nameRu: "Андорра", flag: '🇦🇩' },
  { code: 'AE', alpha3: 'ARE', numeric: '784', dial: '971', name: "United Arab Emirates", nameRu: "ОАЭ", flag: '🇦🇪' },
  { code: 'AF', alpha3: 'AFG', numeric: '004', dial: '93', name: "Afghanistan", nameRu: "Афганистан", flag: '🇦🇫' },
  { code: 'AG', alpha3: 'ATG', numeric: '028', dial: '1', name: "Antigua & Barbuda", nameRu: "Антигуа и Барбуда", flag: '🇦🇬' },
  { code: 'AI', alpha3: 'AIA', numeric: '660', dial: '1', name: "Anguilla", nameRu: "Ангилья", flag: '🇦🇮' },
  { code: 'AL', alpha3: 'ALB', numeric: '008', dial: '355', name: "Albania", nameRu: "Албания", flag: '🇦🇱' },
  { code: 'AM', alpha3: 'ARM', numeric: '051', dial: '374', name: "Armenia", nameRu: "Армения", flag: '🇦🇲' },
  { code: 'AO', alpha3: 'AGO', numeric: '024', dial: '244', name: "Angola", nameRu: "Ангола", flag: '🇦🇴' },
  { code: 'AQ', alpha3: 'ATA', numeric: '010', dial: '', name: "Antarctica", nameRu: "Антарктида", flag: '🇦🇶' },
  { code: 'AR', alpha3: 'ARG', numeric: '032', dial: '54', name: "Argentina", nameRu: "Аргентина", flag: '🇦🇷' },
  { code: 'AS', alpha3: 'ASM', numeric: '016', dial: '1', name: "American Samoa", nameRu: "Американское Самоа", flag: '🇦🇸' },
  { code: 'AT', alpha3: 'AUT', numeric: '040', dial: '43', name: "Austria", nameRu: "Австрия", flag: '🇦🇹' },
  { code: 'AU', alpha3: 'AUS', numeric: '036', dial: '61', name: "Australia", nameRu: "Австралия", flag: '🇦🇺' },
  { code: 'AW', alpha3: 'ABW', numeric: '533', dial: '297', name: "Aruba", nameRu: "Аруба", flag: '🇦🇼' },
  { code: 'AX', alpha3: 'ALA', numeric: '248', dial: '358', name: "Åland Islands", nameRu: "Аландские о-ва", flag: '🇦🇽' },
  { code: 'AZ', alpha3: 'AZE', numeric: '031', dial: '994', name: "Azerbaijan", nameRu: "Азербайджан", flag: '🇦🇿' },
  { code: 'BA', alpha3: 'BIH', numeric: '070', dial: '387', name: "Bosnia & Herzegovina", nameRu: "Босния и Герцеговина", flag: '🇧🇦' },
  { code: 'BB', alpha3: 'BRB', numeric: '052', dial: '1', name: "Barbados", nameRu: "Барбадос", flag: '🇧🇧' },
  { code: 'BD', alpha3: 'BGD', numeric: '050', dial: '880', name: "Bangladesh", nameRu: "Бангладеш", flag: '🇧🇩' },
  { code: 'BE', alpha3: 'BEL', numeric: '056', dial: '32', name: "Belgium", nameRu: "Бельгия", flag: '🇧🇪' },
  { code: 'BF', alpha3: 'BFA', numeric: '854', dial: '226', name: "Burkina Faso", nameRu: "Буркина-Фасо", flag: '🇧🇫' },
  { code: 'BG', alpha3: 'BGR', numeric: '100', dial: '359', name: "Bulgaria", nameRu: "Болгария", flag: '🇧🇬' },
  { code: 'BH', alpha3: 'BHR', numeric: '048', dial: '973', name: "Bahrain", nameRu: "Бахрейн", flag: '🇧🇭' },
  { code: 'BI', alpha3: 'BDI', numeric: '108', dial: '257', name: "Burundi", nameRu: "Бурунди", flag: '🇧🇮' },
  { code: 'BJ', alpha3: 'BEN', numeric: '204', dial: '229', name: "Benin", nameRu: "Бенин", flag: '🇧🇯' },
  { code: 'BL', alpha3: 'BLM', numeric: '652', dial: '590', name: "St. Barthélemy", nameRu: "Сен-Бартелеми", flag: '🇧🇱' },
  { code: 'BM', alpha3: 'BMU', numeric: '060', dial: '1', name: "Bermuda", nameRu: "Бермудские о-ва", flag: '🇧🇲' },
  { code: 'BN', alpha3: 'BRN', numeric: '096', dial: '673', name: "Brunei", nameRu: "Бруней", flag: '🇧🇳' },
  { code: 'BO', alpha3: 'BOL', numeric: '068', dial: '591', name: "Bolivia", nameRu: "Боливия", flag: '🇧🇴' },
  { code: 'BQ', alpha3: 'BES', numeric: '535', dial: '599', name: "Caribbean Netherlands", nameRu: "Бонэйр, Синт-Эстатиус и Саба", flag: '🇧🇶' },
  { code: 'BR', alpha3: 'BRA', numeric: '076', dial: '55', name: "Brazil", nameRu: "Бразилия", flag: '🇧🇷' },
  { code: 'BS', alpha3: 'BHS', numeric: '044', dial: '1', name: "Bahamas", nameRu: "Багамы", flag: '🇧🇸' },
  { code: 'BT', alpha3: 'BTN', numeric: '064', dial: '975', name: "Bhutan", nameRu: "Бутан", flag: '🇧🇹' },
  { code: 'BV', alpha3: 'BVT', numeric: '074', dial: '', name: "Bouvet Island", nameRu: "о-в Буве", flag: '🇧🇻' },
  { code: 'BW', alpha3: 'BWA', numeric: '072', dial: '267', name: "Botswana", nameRu: "Ботсвана", flag: '🇧🇼' },
  { code: 'BY', alpha3: 'BLR', numeric: '112', dial: '375', name: "Belarus", nameRu: "Беларусь", flag: '🇧🇾' },
  { code: 'BZ', alpha3: 'BLZ', numeric: '084', dial: '501', name: "Belize", nameRu: "Белиз", flag: '🇧🇿' },
  { code: 'CA', alpha3: 'CAN', numeric: '124', dial: '1', name: "Canada", nameRu: "Канада", flag: '🇨🇦' },
  { code: 'CC', alpha3: 'CCK', numeric: '166', dial: '61', name: "Cocos (Keeling) Islands", nameRu: "Кокосовые о-ва", flag: '🇨🇨' },
  { code: 'CD', alpha3: 'COD', numeric: '180', dial: '243', name: "Congo - Kinshasa", nameRu: "Конго - Киншаса", flag: '🇨🇩' },
  { code: 'CF', alpha3: 'CAF', numeric: '140', dial: '236', name: "Central African Republic", nameRu: "Центрально-Африканская Республика", flag: '🇨🇫' },
  { code: 'CG', alpha3: 'COG', numeric: '178', dial: '242', name: "Congo - Brazzaville", nameRu: "Конго - Браззавиль", flag: '🇨🇬' },
  { code: 'CH', alpha3: 'CHE', numeric: '756', dial: '41', name: "Switzerland", nameRu: "Швейцария", flag: '🇨🇭' },
  { code: 'CI', alpha3: 'CIV', numeric: '384', dial: '225', name: "Côte d’Ivoire", nameRu: "Кот-д’Ивуар", flag: '🇨🇮' },
  { code: 'CK', alpha3: 'COK', numeric: '184', dial: '682', name: "Cook Islands", nameRu: "о-ва Кука", flag: '🇨🇰' },
  { code: 'CL', alpha3: 'CHL', numeric: '152', dial: '56', name: "Chile", nameRu: "Чили", flag: '🇨🇱' },
  { code: 'CM', alpha3: 'CMR', numeric: '120', dial: '237', name: "Cameroon", nameRu: "Камерун", flag: '🇨🇲' },
  { code: 'CN', alpha3: 'CHN', numeric: '156', dial: '86', name: "China", nameRu: "Китай", flag: '🇨🇳' },
  { code: 'CO', alpha3: 'COL', numeric: '170', dial: '57', name: "Colombia", nameRu: "Колумбия", flag: '🇨🇴' },
  { code: 'CR', alpha3: 'CRI', numeric: '188', dial: '506', name: "Costa Rica", nameRu: "Коста-Рика", flag: '🇨🇷' },
  { code: 'CU', alpha3: 'CUB', numeric: '192', dial: '53', name: "Cuba", nameRu: "Куба", flag: '🇨🇺' },
  { code: 'CV', alpha3: 'CPV', numeric: '132', dial: '238', name: "Cape Verde", nameRu: "Кабо-Верде", flag: '🇨🇻' },
  { code: 'CW', alpha3: 'CUW', numeric: '531', dial: '599', name: "Curaçao", nameRu: "Кюрасао", flag: '🇨🇼' },
  { code: 'CX', alpha3: 'CXR', numeric: '162', dial: '61', name: "Christmas Island", nameRu: "о-в Рождества", flag: '🇨🇽' },
  { code: 'CY', alpha3: 'CYP', numeric: '196', dial: '357', name: "Cyprus", nameRu: "Кипр", flag: '🇨🇾' },
  { code: 'CZ', alpha3: 'CZE', numeric: '203', dial: '420', name: "Czechia", nameRu: "Чехия", flag: '🇨🇿' },
  { code: 'DE', alpha3: 'DEU', numeric: '276', dial: '49', name: "Germany", nameRu: "Германия", flag: '🇩🇪' },
  { code: 'DJ', alpha3: 'DJI', numeric: '262', dial: '253', name: "Djibouti", nameRu: "Джибути", flag: '🇩🇯' },
  { code: 'DK', alpha3: 'DNK', numeric: '208', dial: '45', name: "Denmark", nameRu: "Дания", flag: '🇩🇰' },
  { code: 'DM', alpha3: 'DMA', numeric: '212', dial: '1', name: "Dominica", nameRu: "Доминика", flag: '🇩🇲' },
  { code: 'DO', alpha3: 'DOM', numeric: '214', dial: '1', name: "Dominican Republic", nameRu: "Доминиканская Республика", flag: '🇩🇴' },
  { code: 'DZ', alpha3: 'DZA', numeric: '012', dial: '213', name: "Algeria", nameRu: "Алжир", flag: '🇩🇿' },
  { code: 'EC', alpha3: 'ECU', numeric: '218', dial: '593', name: "Ecuador", nameRu: "Эквадор", flag: '🇪🇨' },
  { code: 'EE', alpha3: 'EST', numeric: '233', dial: '372', name: "Estonia", nameRu: "Эстония", flag: '🇪🇪' },
  { code: 'EG', alpha3: 'EGY', numeric: '818', dial: '20', name: "Egypt", nameRu: "Египет", flag: '🇪🇬' },
  { code: 'EH', alpha3: 'ESH', numeric: '732', dial: '212', name: "Western Sahara", nameRu: "Западная Сахара", flag: '🇪🇭' },
  { code: 'ER', alpha3: 'ERI', numeric: '232', dial: '291', name: "Eritrea", nameRu: "Эритрея", flag: '🇪🇷' },
  { code: 'ES', alpha3: 'ESP', numeric: '724', dial: '34', name: "Spain", nameRu: "Испания", flag: '🇪🇸' },
  { code: 'ET', alpha3: 'ETH', numeric: '231', dial: '251', name: "Ethiopia", nameRu: "Эфиопия", flag: '🇪🇹' },
  { code: 'FI', alpha3: 'FIN', numeric: '246', dial: '358', name: "Finland", nameRu: "Финляндия", flag: '🇫🇮' },
  { code: 'FJ', alpha3: 'FJI', numeric: '242', dial: '679', name: "Fiji", nameRu: "Фиджи", flag: '🇫🇯' },
  { code: 'FK', alpha3: 'FLK', numeric: '238', dial: '500', name: "Falkland Islands", nameRu: "Фолклендские о-ва", flag: '🇫🇰' },
  { code: 'FM', alpha3: 'FSM', numeric: '583', dial: '691', name: "Micronesia", nameRu: "Федеративные Штаты Микронезии", flag: '🇫🇲' },
  { code: 'FO', alpha3: 'FRO', numeric: '234', dial: '298', name: "Faroe Islands", nameRu: "Фарерские о-ва", flag: '🇫🇴' },
  { code: 'FR', alpha3: 'FRA', numeric: '250', dial: '33', name: "France", nameRu: "Франция", flag: '🇫🇷' },
  { code: 'GA', alpha3: 'GAB', numeric: '266', dial: '241', name: "Gabon", nameRu: "Габон", flag: '🇬🇦' },
  { code: 'GB', alpha3: 'GBR', numeric: '826', dial: '44', name: "United Kingdom", nameRu: "Великобритания", flag: '🇬🇧' },
  { code: 'GD', alpha3: 'GRD', numeric: '308', dial: '1', name: "Grenada", nameRu: "Гренада", flag: '🇬🇩' },
  { code: 'GE', alpha3: 'GEO', numeric: '268', dial: '995', name: "Georgia", nameRu: "Грузия", flag: '🇬🇪' },
  { code: 'GF', alpha3: 'GUF', numeric: '254', dial: '594', name: "French Guiana", nameRu: "Французская Гвиана", flag: '🇬🇫' },
  { code: 'GG', alpha3: 'GGY', numeric: '831', dial: '44', name: "Guernsey", nameRu: "Гернси", flag: '🇬🇬' },
  { code: 'GH', alpha3: 'GHA', numeric: '288', dial: '233', name: "Ghana", nameRu: "Гана", flag: '🇬🇭' },
  { code: 'GI', alpha3: 'GIB', numeric: '292', dial: '350', name: "Gibraltar", nameRu: "Гибралтар", flag: '🇬🇮' },
  { code: 'GL', alpha3: 'GRL', numeric: '304', dial: '299', name: "Greenland", nameRu: "Гренландия", flag: '🇬🇱' },
  { code: 'GM', alpha3: 'GMB', numeric: '270', dial: '220', name: "Gambia", nameRu: "Гамбия", flag: '🇬🇲' },
  { code: 'GN', alpha3: 'GIN', numeric: '324', dial: '224', name: "Guinea", nameRu: "Гвинея", flag: '🇬🇳' },
  { code: 'GP', alpha3: 'GLP', numeric: '312', dial: '590', name: "Guadeloupe", nameRu: "Гваделупа", flag: '🇬🇵' },
  { code: 'GQ', alpha3: 'GNQ', numeric: '226', dial: '240', name: "Equatorial Guinea", nameRu: "Экваториальная Гвинея", flag: '🇬🇶' },
  { code: 'GR', alpha3: 'GRC', numeric: '300', dial: '30', name: "Greece", nameRu: "Греция", flag: '🇬🇷' },
  { code: 'GS', alpha3: 'SGS', numeric: '239', dial: '', name: "South Georgia & South Sandwich Islands", nameRu: "Южная Георгия и Южные Сандвичевы о-ва", flag: '🇬🇸' },
  { code: 'GT', alpha3: 'GTM', numeric: '320', dial: '502', name: "Guatemala", nameRu: "Гватемала", flag: '🇬🇹' },
  { code: 'GU', alpha3: 'GUM', numeric: '316', dial: '1', name: "Guam", nameRu: "Гуам", flag: '🇬🇺' },
  { code: 'GW', alpha3: 'GNB', numeric: '624', dial: '245', name: "Guinea-Bissau", nameRu: "Гвинея-Бисау", flag: '🇬🇼' },
  { code: 'GY', alpha3: 'GUY', numeric: '328', dial: '592', name: "Guyana", nameRu: "Гайана", flag: '🇬🇾' },
  { code: 'HK', alpha3: 'HKG', numeric: '344', dial: '852', name: "Hong Kong SAR China", nameRu: "Гонконг (САР)", flag: '🇭🇰' },
  { code: 'HM', alpha3: 'HMD', numeric: '334', dial: '', name: "Heard & McDonald Islands", nameRu: "о-ва Херд и Макдональд", flag: '🇭🇲' },
  { code: 'HN', alpha3: 'HND', numeric: '340', dial: '504', name: "Honduras", nameRu: "Гондурас", flag: '🇭🇳' },
  { code: 'HR', alpha3: 'HRV', numeric: '191', dial: '385', name: "Croatia", nameRu: "Хорватия", flag: '🇭🇷' },
  { code: 'HT', alpha3: 'HTI', numeric: '332', dial: '509', name: "Haiti", nameRu: "Гаити", flag: '🇭🇹' },
  { code: 'HU', alpha3: 'HUN', numeric: '348', dial: '36', name: "Hungary", nameRu: "Венгрия", flag: '🇭🇺' },
  { code: 'ID', alpha3: 'IDN', numeric: '360', dial: '62', name: "Indonesia", nameRu: "Индонезия", flag: '🇮🇩' },
  { code: 'IE', alpha3: 'IRL', numeric: '372', dial: '353', name: "Ireland", nameRu: "Ирландия", flag: '🇮🇪' },
  { code: 'IL', alpha3: 'ISR', numeric: '376', dial: '972', name: "Israel", nameRu: "Израиль", flag: '🇮🇱' },
  { code: 'IM', alpha3: 'IMN', numeric: '833', dial: '44', name: "Isle of Man", nameRu: "о-в Мэн", flag: '🇮🇲' },
  { code: 'IN', alpha3: 'IND', numeric: '356', dial: '91', name: "India", nameRu: "Индия", flag: '🇮🇳' },
  { code: 'IO', alpha3: 'IOT', numeric: '086', dial: '246', name: "British Indian Ocean Territory", nameRu: "Британская территория в Индийском океане", flag: '🇮🇴' },
  { code: 'IQ', alpha3: 'IRQ', numeric: '368', dial: '964', name: "Iraq", nameRu: "Ирак", flag: '🇮🇶' },
  { code: 'IR', alpha3: 'IRN', numeric: '364', dial: '98', name: "Iran", nameRu: "Иран", flag: '🇮🇷' },
  { code: 'IS', alpha3: 'ISL', numeric: '352', dial: '354', name: "Iceland", nameRu: "Исландия", flag: '🇮🇸' },
  { code: 'IT', alpha3: 'ITA', numeric: '380', dial: '39', name: "Italy", nameRu: "Италия", flag: '🇮🇹' },
  { code: 'JE', alpha3: 'JEY', numeric: '832', dial: '44', name: "Jersey", nameRu: "Джерси", flag: '🇯🇪' },
  { code: 'JM', alpha3: 'JAM', numeric: '388', dial: '1', name: "Jamaica", nameRu: "Ямайка", flag: '🇯🇲' },
  { code: 'JO', alpha3: 'JOR', numeric: '400', dial: '962', name: "Jordan", nameRu: "Иордания", flag: '🇯🇴' },
  { code: 'JP', alpha3: 'JPN', numeric: '392', dial: '81', name: "Japan", nameRu: "Япония", flag: '🇯🇵' },
  { code: 'KE', alpha3: 'KEN', numeric: '404', dial: '254', name: "Kenya", nameRu: "Кения", flag: '🇰🇪' },
  { code: 'KG', alpha3: 'KGZ', numeric: '417', dial: '996', name: "Kyrgyzstan", nameRu: "Киргизия", flag: '🇰🇬' },
  { code: 'KH', alpha3: 'KHM', numeric: '116', dial: '855', name: "Cambodia", nameRu: "Камбоджа", flag: '🇰🇭' },
  { code: 'KI', alpha3: 'KIR', numeric: '296', dial: '686', name: "Kiribati", nameRu: "Кирибати", flag: '🇰🇮' },
  { code: 'KM', alpha3: 'COM', numeric: '174', dial: '269', name: "Comoros", nameRu: "Коморы", flag: '🇰🇲' },
  { code: 'KN', alpha3: 'KNA', numeric: '659', dial: '1', name: "St. Kitts & Nevis", nameRu: "Сент-Китс и Невис", flag: '🇰🇳' },
  { code: 'KP', alpha3: 'PRK', numeric: '408', dial: '850', name: "North Korea", nameRu: "КНДР", flag: '🇰🇵' },
  { code: 'KR', alpha3: 'KOR', numeric: '410', dial: '82', name: "South Korea", nameRu: "Республика Корея", flag: '🇰🇷' },
  { code: 'KW', alpha3: 'KWT', numeric: '414', dial: '965', name: "Kuwait", nameRu: "Кувейт", flag: '🇰🇼' },
  { code: 'KY', alpha3: 'CYM', numeric: '136', dial: '1', name: "Cayman Islands", nameRu: "о-ва Кайман", flag: '🇰🇾' },
  { code: 'KZ', alpha3: 'KAZ', numeric: '398', dial: '7', name: "Kazakhstan", nameRu: "Казахстан", flag: '🇰🇿' },
  { code: 'LA', alpha3: 'LAO', numeric: '418', dial: '856', name: "Laos", nameRu: "Лаос", flag: '🇱🇦' },
  { code: 'LB', alpha3: 'LBN', numeric: '422', dial: '961', name: "Lebanon", nameRu: "Ливан", flag: '🇱🇧' },
  { code: 'LC', alpha3: 'LCA', numeric: '662', dial: '1', name: "St. Lucia", nameRu: "Сент-Люсия", flag: '🇱🇨' },
  { code: 'LI', alpha3: 'LIE', numeric: '438', dial: '423', name: "Liechtenstein", nameRu: "Лихтенштейн", flag: '🇱🇮' },
  { code: 'LK', alpha3: 'LKA', numeric: '144', dial: '94', name: "Sri Lanka", nameRu: "Шри-Ланка", flag: '🇱🇰' },
  { code: 'LR', alpha3: 'LBR', numeric: '430', dial: '231', name: "Liberia", nameRu: "Либерия", flag: '🇱🇷' },
  { code: 'LS', alpha3: 'LSO', numeric: '426', dial: '266', name: "Lesotho", nameRu: "Лесото", flag: '🇱🇸' },
  { code: 'LT', alpha3: 'LTU', numeric: '440', dial: '370', name: "Lithuania", nameRu: "Литва", flag: '🇱🇹' },
  { code: 'LU', alpha3: 'LUX', numeric: '442', dial: '352', name: "Luxembourg", nameRu: "Люксембург", flag: '🇱🇺' },
  { code: 'LV', alpha3: 'LVA', numeric: '428', dial: '371', name: "Latvia", nameRu: "Латвия", flag: '🇱🇻' },
  { code: 'LY', alpha3: 'LBY', numeric: '434', dial: '218', name: "Libya", nameRu: "Ливия", flag: '🇱🇾' },
  { code: 'MA', alpha3: 'MAR', numeric: '504', dial: '212', name: "Morocco", nameRu: "Марокко", flag: '🇲🇦' },
  { code: 'MC', alpha3: 'MCO', numeric: '492', dial: '377', name: "Monaco", nameRu: "Монако", flag: '🇲🇨' },
  { code: 'MD', alpha3: 'MDA', numeric: '498', dial: '373', name: "Moldova", nameRu: "Молдова", flag: '🇲🇩' },
  { code: 'ME', alpha3: 'MNE', numeric: '499', dial: '382', name: "Montenegro", nameRu: "Черногория", flag: '🇲🇪' },
  { code: 'MF', alpha3: 'MAF', numeric: '663', dial: '590', name: "St. Martin", nameRu: "Сен-Мартен", flag: '🇲🇫' },
  { code: 'MG', alpha3: 'MDG', numeric: '450', dial: '261', name: "Madagascar", nameRu: "Мадагаскар", flag: '🇲🇬' },
  { code: 'MH', alpha3: 'MHL', numeric: '584', dial: '692', name: "Marshall Islands", nameRu: "Маршалловы о-ва", flag: '🇲🇭' },
  { code: 'MK', alpha3: 'MKD', numeric: '807', dial: '389', name: "North Macedonia", nameRu: "Северная Македония", flag: '🇲🇰' },
  { code: 'ML', alpha3: 'MLI', numeric: '466', dial: '223', name: "Mali", nameRu: "Мали", flag: '🇲🇱' },
  { code: 'MM', alpha3: 'MMR', numeric: '104', dial: '95', name: "Myanmar (Burma)", nameRu: "Мьянма (Бирма)", flag: '🇲🇲' },
  { code: 'MN', alpha3: 'MNG', numeric: '496', dial: '976', name: "Mongolia", nameRu: "Монголия", flag: '🇲🇳' },
  { code: 'MO', alpha3: 'MAC', numeric: '446', dial: '853', name: "Macao SAR China", nameRu: "Макао (САР)", flag: '🇲🇴' },
  { code: 'MP', alpha3: 'MNP', numeric: '580', dial: '1', name: "Northern Mariana Islands", nameRu: "Северные Марианские о-ва", flag: '🇲🇵' },
  { code: 'MQ', alpha3: 'MTQ', numeric: '474', dial: '596', name: "Martinique", nameRu: "Мартиника", flag: '🇲🇶' },
  { code: 'MR', alpha3: 'MRT', numeric: '478', dial: '222', name: "Mauritania", nameRu: "Мавритания", flag: '🇲🇷' },
  { code: 'MS', alpha3: 'MSR', numeric: '500', dial: '1', name: "Montserrat", nameRu: "Монтсеррат", flag: '🇲🇸' },
  { code: 'MT', alpha3: 'MLT', numeric: '470', dial: '356', name: "Malta", nameRu: "Мальта", flag: '🇲🇹' },
  { code: 'MU', alpha3: 'MUS', numeric: '480', dial: '230', name: "Mauritius", nameRu: "Маврикий", flag: '🇲🇺' },
  { code: 'MV', alpha3: 'MDV', numeric: '462', dial: '960', name: "Maldives", nameRu: "Мальдивы", flag: '🇲🇻' },
  { code: 'MW', alpha3: 'MWI', numeric: '454', dial: '265', name: "Malawi", nameRu: "Малави", flag: '🇲🇼' },
  { code: 'MX', alpha3: 'MEX', numeric: '484', dial: '52', name: "Mexico", nameRu: "Мексика", flag: '🇲🇽' },
  { code: 'MY', alpha3: 'MYS', numeric: '458', dial: '60', name: "Malaysia", nameRu: "Малайзия", flag: '🇲🇾' },
  { code: 'MZ', alpha3: 'MOZ', numeric: '508', dial: '258', name: "Mozambique", nameRu: "Мозамбик", flag: '🇲🇿' },
  { code: 'NA', alpha3: 'NAM', numeric: '516', dial: '264', name: "Namibia", nameRu: "Намибия", flag: '🇳🇦' },
  { code: 'NC', alpha3: 'NCL', numeric: '540', dial: '687', name: "New Caledonia", nameRu: "Новая Каледония", flag: '🇳🇨' },
  { code: 'NE', alpha3: 'NER', numeric: '562', dial: '227', name: "Niger", nameRu: "Нигер", flag: '🇳🇪' },
  { code: 'NF', alpha3: 'NFK', numeric: '574', dial: '672', name: "Norfolk Island", nameRu: "о-в Норфолк", flag: '🇳🇫' },
  { code: 'NG', alpha3: 'NGA', numeric: '566', dial: '234', name: "Nigeria", nameRu: "Нигерия", flag: '🇳🇬' },
  { code: 'NI', alpha3: 'NIC', numeric: '558', dial: '505', name: "Nicaragua", nameRu: "Никарагуа", flag: '🇳🇮' },
  { code: 'NL', alpha3: 'NLD', numeric: '528', dial: '31', name: "Netherlands", nameRu: "Нидерланды", flag: '🇳🇱' },
  { code: 'NO', alpha3: 'NOR', numeric: '578', dial: '47', name: "Norway", nameRu: "Норвегия", flag: '🇳🇴' },
  { code: 'NP', alpha3: 'NPL', numeric: '524', dial: '977', name: "Nepal", nameRu: "Непал", flag: '🇳🇵' },
  { code: 'NR', alpha3: 'NRU', numeric: '520', dial: '674', name: "Nauru", nameRu: "Науру", flag: '🇳🇷' },
  { code: 'NU', alpha3: 'NIU', numeric: '570', dial: '683', name: "Niue", nameRu: "Ниуэ", flag: '🇳🇺' },
  { code: 'NZ', alpha3: 'NZL', numeric: '554', dial: '64', name: "New Zealand", nameRu: "Новая Зеландия", flag: '🇳🇿' },
  { code: 'OM', alpha3: 'OMN', numeric: '512', dial: '968', name: "Oman", nameRu: "Оман", flag: '🇴🇲' },
  { code: 'PA', alpha3: 'PAN', numeric: '591', dial: '507', name: "Panama", nameRu: "Панама", flag: '🇵🇦' },
  { code: 'PE', alpha3: 'PER', numeric: '604', dial: '51', name: "Peru", nameRu: "Перу", flag: '🇵🇪' },
  { code: 'PF', alpha3: 'PYF', numeric: '258', dial: '689', name: "French Polynesia", nameRu: "Французская Полинезия", flag: '🇵🇫' },
  { code: 'PG', alpha3: 'PNG', numeric: '598', dial: '675', name: "Papua New Guinea", nameRu: "Папуа — Новая Гвинея", flag: '🇵🇬' },
  { code: 'PH', alpha3: 'PHL', numeric: '608', dial: '63', name: "Philippines", nameRu: "Филиппины", flag: '🇵🇭' },
  { code: 'PK', alpha3: 'PAK', numeric: '586', dial: '92', name: "Pakistan", nameRu: "Пакистан", flag: '🇵🇰' },
  { code: 'PL', alpha3: 'POL', numeric: '616', dial: '48', name: "Poland", nameRu: "Польша", flag: '🇵🇱' },
  { code: 'PM', alpha3: 'SPM', numeric: '666', dial: '508', name: "St. Pierre & Miquelon", nameRu: "Сен-Пьер и Микелон", flag: '🇵🇲' },
  { code: 'PN', alpha3: 'PCN', numeric: '612', dial: '', name: "Pitcairn Islands", nameRu: "о-ва Питкэрн", flag: '🇵🇳' },
  { code: 'PR', alpha3: 'PRI', numeric: '630', dial: '1', name: "Puerto Rico", nameRu: "Пуэрто-Рико", flag: '🇵🇷' },
  { code: 'PS', alpha3: 'PSE', numeric: '275', dial: '970', name: "Palestinian Territories", nameRu: "Палестинские территории", flag: '🇵🇸' },
  { code: 'PT', alpha3: 'PRT', numeric: '620', dial: '351', name: "Portugal", nameRu: "Португалия", flag: '🇵🇹' },
  { code: 'PW', alpha3: 'PLW', numeric: '585', dial: '680', name: "Palau", nameRu: "Палау", flag: '🇵🇼' },
  { code: 'PY', alpha3: 'PRY', numeric: '600', dial: '595', name: "Paraguay", nameRu: "Парагвай", flag: '🇵🇾' },
  { code: 'QA', alpha3: 'QAT', numeric: '634', dial: '974', name: "Qatar", nameRu: "Катар", flag: '🇶🇦' },
  { code: 'RE', alpha3: 'REU', numeric: '638', dial: '262', name: "Réunion", nameRu: "Реюньон", flag: '🇷🇪' },
  { code: 'RO', alpha3: 'ROU', numeric: '642', dial: '40', name: "Romania", nameRu: "Румыния", flag: '🇷🇴' },
  { code: 'RS', alpha3: 'SRB', numeric: '688', dial: '381', name: "Serbia", nameRu: "Сербия", flag: '🇷🇸' },
  { code: 'RU', alpha3: 'RUS', numeric: '643', dial: '7', name: "Russia", nameRu: "Россия", flag: '🇷🇺' },
  { code: 'RW', alpha3: 'RWA', numeric: '646', dial: '250', name: "Rwanda", nameRu: "Руанда", flag: '🇷🇼' },
  { code: 'SA', alpha3: 'SAU', numeric: '682', dial: '966', name: "Saudi Arabia", nameRu: "Саудовская Аравия", flag: '🇸🇦' },
  { code: 'SB', alpha3: 'SLB', numeric: '090', dial: '677', name: "Solomon Islands", nameRu: "Соломоновы о-ва", flag: '🇸🇧' },
  { code: 'SC', alpha3: 'SYC', numeric: '690', dial: '248', name: "Seychelles", nameRu: "Сейшельские о-ва", flag: '🇸🇨' },
  { code: 'SD', alpha3: 'SDN', numeric: '729', dial: '249', name: "Sudan", nameRu: "Судан", flag: '🇸🇩' },
  { code: 'SE', alpha3: 'SWE', numeric: '752', dial: '46', name: "Sweden", nameRu: "Швеция", flag: '🇸🇪' },
  { code: 'SG', alpha3: 'SGP', numeric: '702', dial: '65', name: "Singapore", nameRu: "Сингапур", flag: '🇸🇬' },
  { code: 'SH', alpha3: 'SHN', numeric: '654', dial: '290', name: "St. Helena", nameRu: "о-в Св. Елены", flag: '🇸🇭' },
  { code: 'SI', alpha3: 'SVN', numeric: '705', dial: '386', name: "Slovenia", nameRu: "Словения", flag: '🇸🇮' },
  { code: 'SJ', alpha3: 'SJM', numeric: '744', dial: '47', name: "Svalbard & Jan Mayen", nameRu: "Шпицберген и Ян-Майен", flag: '🇸🇯' },
  { code: 'SK', alpha3: 'SVK', numeric: '703', dial: '421', name: "Slovakia", nameRu: "Словакия", flag: '🇸🇰' },
  { code: 'SL', alpha3: 'SLE', numeric: '694', dial: '232', name: "Sierra Leone", nameRu: "Сьерра-Леоне", flag: '🇸🇱' },
  { code: 'SM', alpha3: 'SMR', numeric: '674', dial: '378', name: "San Marino", nameRu: "Сан-Марино", flag: '🇸🇲' },
  { code: 'SN', alpha3: 'SEN', numeric: '686', dial: '221', name: "Senegal", nameRu: "Сенегал", flag: '🇸🇳' },
  { code: 'SO', alpha3: 'SOM', numeric: '706', dial: '252', name: "Somalia", nameRu: "Сомали", flag: '🇸🇴' },
  { code: 'SR', alpha3: 'SUR', numeric: '740', dial: '597', name: "Suriname", nameRu: "Суринам", flag: '🇸🇷' },
  { code: 'SS', alpha3: 'SSD', numeric: '728', dial: '211', name: "South Sudan", nameRu: "Южный Судан", flag: '🇸🇸' },
  { code: 'ST', alpha3: 'STP', numeric: '678', dial: '239', name: "São Tomé & Príncipe", nameRu: "Сан-Томе и Принсипи", flag: '🇸🇹' },
  { code: 'SV', alpha3: 'SLV', numeric: '222', dial: '503', name: "El Salvador", nameRu: "Сальвадор", flag: '🇸🇻' },
  { code: 'SX', alpha3: 'SXM', numeric: '534', dial: '1', name: "Sint Maarten", nameRu: "Синт-Мартен", flag: '🇸🇽' },
  { code: 'SY', alpha3: 'SYR', numeric: '760', dial: '963', name: "Syria", nameRu: "Сирия", flag: '🇸🇾' },
  { code: 'SZ', alpha3: 'SWZ', numeric: '748', dial: '268', name: "Eswatini", nameRu: "Эсватини", flag: '🇸🇿' },
  { code: 'TC', alpha3: 'TCA', numeric: '796', dial: '1', name: "Turks & Caicos Islands", nameRu: "Тёркс и Кайкос", flag: '🇹🇨' },
  { code: 'TD', alpha3: 'TCD', numeric: '148', dial: '235', name: "Chad", nameRu: "Чад", flag: '🇹🇩' },
  { code: 'TF', alpha3: 'ATF', numeric: '260', dial: '', name: "French Southern Territories", nameRu: "Французские Южные территории", flag: '🇹🇫' },
  { code: 'TG', alpha3: 'TGO', numeric: '768', dial: '228', name: "Togo", nameRu: "Того", flag: '🇹🇬' },
  { code: 'TH', alpha3: 'THA', numeric: '764', dial: '66', name: "Thailand", nameRu: "Таиланд", flag: '🇹🇭' },
  { code: 'TJ', alpha3: 'TJK', numeric: '762', dial: '992', name: "Tajikistan", nameRu: "Таджикистан", flag: '🇹🇯' },
  { code: 'TK', alpha3: 'TKL', numeric: '772', dial: '690', name: "Tokelau", nameRu: "Токелау", flag: '🇹🇰' },
  { code: 'TL', alpha3: 'TLS', numeric: '626', dial: '670', name: "Timor-Leste", nameRu: "Восточный Тимор", flag: '🇹🇱' },
  { code: 'TM', alpha3: 'TKM', numeric: '795', dial: '993', name: "Turkmenistan", nameRu: "Туркменистан", flag: '🇹🇲' },
  { code: 'TN', alpha3: 'TUN', numeric: '788', dial: '216', name: "Tunisia", nameRu: "Тунис", flag: '🇹🇳' },
  { code: 'TO', alpha3: 'TON', numeric: '776', dial: '676', name: "Tonga", nameRu: "Тонга", flag: '🇹🇴' },
  { code: 'TR', alpha3: 'TUR', numeric: '792', dial: '90', name: "Türkiye", nameRu: "Турция", flag: '🇹🇷' },
  { code: 'TT', alpha3: 'TTO', numeric: '780', dial: '1', name: "Trinidad & Tobago", nameRu: "Тринидад и Тобаго", flag: '🇹🇹' },
  { code: 'TV', alpha3: 'TUV', numeric: '798', dial: '688', name: "Tuvalu", nameRu: "Тувалу", flag: '🇹🇻' },
  { code: 'TW', alpha3: 'TWN', numeric: '158', dial: '886', name: "Taiwan", nameRu: "Тайвань", flag: '🇹🇼' },
  { code: 'TZ', alpha3: 'TZA', numeric: '834', dial: '255', name: "Tanzania", nameRu: "Танзания", flag: '🇹🇿' },
  { code: 'UA', alpha3: 'UKR', numeric: '804', dial: '380', name: "Ukraine", nameRu: "Украина", flag: '🇺🇦' },
  { code: 'UG', alpha3: 'UGA', numeric: '800', dial: '256', name: "Uganda", nameRu: "Уганда", flag: '🇺🇬' },
  { code: 'UM', alpha3: 'UMI', numeric: '581', dial: '', name: "U.S. Outlying Islands", nameRu: "Внешние малые о-ва (США)", flag: '🇺🇲' },
  { code: 'US', alpha3: 'USA', numeric: '840', dial: '1', name: "United States", nameRu: "Соединенные Штаты", flag: '🇺🇸' },
  { code: 'UY', alpha3: 'URY', numeric: '858', dial: '598', name: "Uruguay", nameRu: "Уругвай", flag: '🇺🇾' },
  { code: 'UZ', alpha3: 'UZB', numeric: '860', dial: '998', name: "Uzbekistan", nameRu: "Узбекистан", flag: '🇺🇿' },
  { code: 'VA', alpha3: 'VAT', numeric: '336', dial: '39', name: "Vatican City", nameRu: "Ватикан", flag: '🇻🇦' },
  { code: 'VC', alpha3: 'VCT', numeric: '670', dial: '1', name: "St. Vincent & Grenadines", nameRu: "Сент-Винсент и Гренадины", flag: '🇻🇨' },
  { code: 'VE', alpha3: 'VEN', numeric: '862', dial: '58', name: "Venezuela", nameRu: "Венесуэла", flag: '🇻🇪' },
  { code: 'VG', alpha3: 'VGB', numeric: '092', dial: '1', name: "British Virgin Islands", nameRu: "Виргинские о-ва (Великобритания)", flag: '🇻🇬' },
  { code: 'VI', alpha3: 'VIR', numeric: '850', dial: '1', name: "U.S. Virgin Islands", nameRu: "Виргинские о-ва (США)", flag: '🇻🇮' },
  { code: 'VN', alpha3: 'VNM', numeric: '704', dial: '84', name: "Vietnam", nameRu: "Вьетнам", flag: '🇻🇳' },
  { code: 'VU', alpha3: 'VUT', numeric: '548', dial: '678', name: "Vanuatu", nameRu: "Вануату", flag: '🇻🇺' },
  { code: 'WF', alpha3: 'WLF', numeric: '876', dial: '681', name: "Wallis & Futuna", nameRu: "Уоллис и Футуна", flag: '🇼🇫' },
  { code: 'WS', alpha3: 'WSM', numeric: '882', dial: '685', name: "Samoa", nameRu: "Самоа", flag: '🇼🇸' },
  { code: 'YE', alpha3: 'YEM', numeric: '887', dial: '967', name: "Yemen", nameRu: "Йемен", flag: '🇾🇪' },
  { code: 'YT', alpha3: 'MYT', numeric: '175', dial: '262', name: "Mayotte", nameRu: "Майотта", flag: '🇾🇹' },
  { code: 'ZA', alpha3: 'ZAF', numeric: '710', dial: '27', name: "South Africa", nameRu: "Южно-Африканская Республика", flag: '🇿🇦' },
  { code: 'ZM', alpha3: 'ZMB', numeric: '894', dial: '260', name: "Zambia", nameRu: "Замбия", flag: '🇿🇲' },
  { code: 'ZW', alpha3: 'ZWE', numeric: '716', dial: '263', name: "Zimbabwe", nameRu: "Зимбабве", flag: '🇿🇼' },
]

/* The lanes ASA actually runs, floated to the top of the picker.
 *
 * Membership of the list is the standard's; ORDER is ours. Nearly every quote
 * this product will ever hold moves between these — the China–Central Asia rail
 * corridors and the road lanes off them — and a picker that opens on Andorra
 * makes the common case the one that needs scrolling. The rest of the world
 * follows alphabetically, so nothing is unreachable and nothing is hidden.
 *
 * Sorted by the CORRIDOR, not alphabetically: origin countries first, then the
 * transit and destination markets, which is the order a routing is entered in.
 *
 * Wrong entries here cost a scroll, never a missing country. Extend it as the
 * lanes change. */
export const FREQUENT_COUNTRY_CODES: readonly string[] = [
  // Origins.
  'CN', 'TR', 'RU', 'AE', 'IN', 'IR',
  // Transit and destination — Central Asia and the Caucasus.
  'KZ', 'UZ', 'TM', 'KG', 'TJ', 'AF', 'AZ', 'GE', 'AM',
  // Europe and the rest of the regular book.
  'BY', 'UA', 'PL', 'DE', 'LT', 'LV', 'NL', 'KR', 'JP', 'US',
]

const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]))

/** The country a stored value names, or undefined when it names something else
 *  — a port, a city, or a spelling from the workbook. Case-insensitive, because
 *  a hand-written payload may hold "cn". */
export function findCountry(code: string | undefined | null): Country | undefined {
  if (!code) return undefined
  return BY_CODE.get(code.trim().toUpperCase())
}

/** Whether a stored value is one of the 249 codes.
 *
 *  Used to decide whether a route option's origin can be shown in the picker or
 *  has to be offered as-is: a Select whose value is absent from its options
 *  renders an empty control, which over a saved routing reads as "no origin
 *  agreed" rather than as "this predates the picker". */
export function isCountryCode(value: string | undefined | null): boolean {
  return findCountry(value) !== undefined
}

/** How a country reads in the interface: the flag, then the name in the active
 *  language. The CODE is deliberately not in the label — it is the value and it
 *  is shown as the hint, the same split incoterms.ts uses, because "Kazakhstan"
 *  is what a salesperson scans for and "KZ" is what the record holds. */
export function countryName(country: Country, language: string): string {
  return language.startsWith('ru') ? country.nameRu : country.name
}

/** How a STORED routing endpoint reads on screen: the localised country name if
 *  the value is one of the 249 codes, and the value itself if it is not.
 *
 *  Every surface that prints an origin or a destination goes through this, and
 *  that is the point. Those columns now hold a mix of two generations — "KZ"
 *  from the picker, "Ashgabat" from before it and from the importer — and a
 *  screen that printed the raw column would read "KZ → CN", which is a database
 *  row rather than a lane a salesperson recognises. The carry-through of an
 *  unrecognised value is the same rule CountryDropdown applies to the field
 *  itself: nothing here rejects or hides data that is already saved.
 *
 *  Blank in, undefined out, so a caller can filter empty ends out of a
 *  "A → B → C" join without also testing for whitespace. */
export function placeName(
  value: string | undefined | null,
  language: string,
): string | undefined {
  const stored = (value ?? '').trim()
  if (stored === '') return undefined
  const country = findCountry(stored)
  return country ? countryName(country, language) : stored
}

/** The picker's options, frequent lanes first.
 *
 *  Matching is on BOTH languages and BOTH code forms regardless of the active
 *  one: a Russian-speaking agent quoting a Chinese supplier types "china" as
 *  often as "Китай", and an operator reading a customs form searches "RUS".
 *  Filtering that only saw the active language would answer "no matches" to a
 *  correctly spelled country. */
export function countryOptions(language: string): {
  value: string
  label: string
  hint: string
  /** Everything the filter should match, lowercased. */
  search: string
}[] {
  const rank = new Map(
    FREQUENT_COUNTRY_CODES.map((code, index) => [code, index]),
  )

  const ordered = [...COUNTRIES].sort((a, b) => {
    const ra = rank.get(a.code)
    const rb = rank.get(b.code)
    if (ra != null && rb != null) return ra - rb
    if (ra != null) return -1
    if (rb != null) return 1
    // localeCompare, not <: "Åland" and "Österreich" sort where a reader
    // expects only if the collator decides, and the RU list is Cyrillic.
    return countryName(a, language).localeCompare(countryName(b, language), language)
  })

  return ordered.map((country) => ({
    value: country.code,
    /* The NAME alone. The flag used to be glued onto the front of this string
       as an emoji; it is now drawn beside the row by CircleFlag, from an SVG
       that renders on Windows too. A label that still carried the glyph would
       put a flag that is a boxed "KZ" next to a flag that is a flag. */
    label: countryName(country, language),
    hint: country.code,
    search: [
      country.name,
      country.nameRu,
      country.code,
      country.alpha3,
      country.numeric,
    ]
      .join(' ')
      .toLowerCase(),
  }))
}

/* ── Telephone country codes ───────────────────────────────────────────────
 *
 * The contact phone was a bare `<input type="tel">`, which is how the same
 * mobile ends up stored as "+7 701 …", "8 701 …" and "701 …" in three rows of
 * the same directory. The picker in front of it makes the country an explicit
 * choice rather than a prefix the typist may or may not have remembered.
 *
 * What is NOT here: validation, formatting, or a national-number parser. That is
 * libphonenumber's job and it costs ~150 KB to do it; the value stays free text
 * on the server (a contact's phone is a string, not a domain type), and a
 * brokerage's address book legitimately holds extensions, second numbers and
 * "ask for Dmitry". Only the DIAL CODE is structured, and only because it is the
 * part a person gets wrong.
 */

/** The countries a phone number can be dialled in — the 249 minus the seven
 *  uninhabited territories, which have no calling code. */
export const PHONE_COUNTRIES: readonly Country[] = COUNTRIES.filter(
  (country) => country.dial !== '',
)

/* One country per calling code, for reading a stored number back.
 *
 * Twenty-odd calling codes are shared — +7 is Russia and Kazakhstan, +1 is North
 * America and the Caribbean, +44 is the UK and three Crown dependencies — so a
 * number alone does not name a country and the picker has to pick one. The
 * default matters only for which flag is shown beside an already-saved number;
 * the user can always say otherwise, and doing so does not change the digits.
 *
 * KZ for +7 because this is a Kazakh brokerage and most +7 numbers in its
 * address book are local ones. Everything else is the ITU's main territory. */
const PHONE_MAIN_COUNTRY: Record<string, string> = {
  '7': 'KZ',
  '1': 'US',
  '44': 'GB',
  '39': 'IT',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
  '672': 'NF',
}

/* Longest first. No calling code in the ITU list is a prefix of another — every
   NANP country is plain "1", Antigua included — so today the order changes
   nothing. It is here as the cheap guard for the day somebody hand-adds an
   area-code-level entry, where matching the shortest would file every Antiguan
   number as American. */
const DIAL_CODES: readonly string[] = [
  ...new Set(PHONE_COUNTRIES.map((country) => country.dial)),
].sort((a, b) => b.length - a.length)

const BY_DIAL = new Map<string, Country>()
for (const dial of DIAL_CODES) {
  const preferred = PHONE_MAIN_COUNTRY[dial]
  const country = preferred
    ? PHONE_COUNTRIES.find((c) => c.code === preferred)
    : undefined
  BY_DIAL.set(
    dial,
    country ?? PHONE_COUNTRIES.find((c) => c.dial === dial)!,
  )
}

/** What a stored phone string is made of: the country whose code it opens with,
 *  and everything after that code exactly as it was typed. */
export interface PhoneParts {
  /** undefined when the value carries no recognisable "+code" — a bare local
   *  number, or one of the pre-picker rows that starts with 8. */
  country: Country | undefined
  /** The rest, untouched: spaces, dashes, extensions and all. */
  national: string
}

/** Splits a stored number into its country and the rest.
 *
 *  Only a leading `+` is treated as a country code. "8 701 …" is a Russian or
 *  Kazakh national form and "701 …" is a local one; guessing a country for
 *  either would rewrite somebody's address book on the first edit, so both come
 *  back with no country and are left in the field as they are. */
export function splitPhone(value: string | undefined | null): PhoneParts {
  /* trimStart, NOT trim. A trailing space is what a person has just typed
     between two groups of digits, and a round trip through this function that
     ate it would swallow every space as it was pressed — "701 234" would come
     back "701234". Leading space is noise from a paste and goes. */
  const trimmed = (value ?? '').trimStart()
  if (!trimmed.startsWith('+')) return { country: undefined, national: trimmed }

  const digits = trimmed.slice(1)
  for (const dial of DIAL_CODES) {
    if (digits.startsWith(dial)) {
      return {
        country: BY_DIAL.get(dial),
        national: digits.slice(dial.length).trimStart(),
      }
    }
  }
  return { country: undefined, national: trimmed }
}

/** Puts one back together. Blank in, blank out — a contact row with a country
 *  selected but no digits typed is an EMPTY phone, not "+7", because "+7" saved
 *  into a directory reads as a phone number that fails to dial. */
export function joinPhone(
  country: Country | undefined,
  national: string,
): string {
  const rest = national.trimStart()
  if (rest === '') return ''
  if (!country) return rest
  return `+${country.dial} ${rest}`
}

/** The phone picker's options, frequent lanes first — the same ordering
 *  `countryOptions` uses, with the calling code as the hint and as a further
 *  thing the filter matches: "+993", "993" and "Turkmenistan" all land on the
 *  same row. */
export function phoneOptions(language: string): {
  value: string
  label: string
  hint: string
  search: string
}[] {
  const dialable = new Set(PHONE_COUNTRIES.map((country) => country.code))
  return countryOptions(language)
    .filter((option) => dialable.has(option.value))
    .map((option) => {
      const country = findCountry(option.value)!
      return {
        ...option,
        hint: `+${country.dial}`,
        search: `${option.search} +${country.dial} ${country.dial}`,
      }
    })
}
